import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import moment from "moment-timezone";
import { Tooltip } from "react-tooltip";
import { getNotificationActivityLogs } from "../../modules/Notification";
import { showSpinner, closeSpinner, modal, alert } from "../../modules/Dialogs";
import { sessionExpired } from "../../config/GeneralConfig";
import { toScreen } from "../../modules/Routing";
import NotificationLogDetails from "./NotificationLogDetails";

const COLUMNS = [
  { key: "channel",           label: "Channel Id",          class: "nl-col-channel",    sortable: false },
  { key: "recipient",         label: "Recipient",            class: "nl-col-recipient",  sortable: false },
  { key: "status",            label: "Status",               class: "nl-col-status",     sortable: false },
  { key: "provider",          label: "Provider",             class: "nl-col-provider",   sortable: false },
  { key: "providerMessageId", label: "Provider Message ID",  class: "nl-col-msg-id",     sortable: false },
  { key: "triggeredAt",       label: "Triggered At",         class: "nl-col-triggered",  sortable: true },
];

const STATUS_BADGE = {
  sent:      "badge-success",
  failed:    "badge-danger",
  abandoned: "badge-warning",
  queued:    "badge-info",
  delivered: "badge-success",
};

const FILTER_OPTIONS = [
  { id: "noti_all",       label: "All",       value: "all" },
  { id: "noti_sent",      label: "Sent",      value: "sent" },
  { id: "noti_failed",    label: "Failed",    value: "failed" },
  { id: "noti_abandoned", label: "Abandoned", value: "abandoned" },
  { id: "noti_queued",    label: "Queued",    value: "queued" },
];

const PAGE_SIZE = 15;

const NotificationLogsTab = ({ userDetails, navigate }) => {
  const [data, setData]                         = useState([]);
  const [inputSearchValue, setInputSearchValue] = useState("");
  const [searchValue, setSearchValue]           = useState("");
  const [filter, setFilter]                     = useState("all");
  const [sort, setSort]                         = useState("createdAt=-1");
  const [nextCursor, setNextCursor]             = useState(null);
  const [hasNextPage, setHasNextPage]           = useState(false);
  const [cursorStack, setCursorStack]           = useState([null]);
  const [isLoadingMore, setIsLoadingMore]       = useState(false);
  const [showDetails, setShowDetails]           = useState(false);
  const [selectedLog, setSelectedLog]           = useState(null);

  const fetchData = useCallback((cursor = null, append = false) => {
    if (!append) showSpinner();
    setIsLoadingMore(true);

    const payload = { pageSize: PAGE_SIZE };

    if (sort) {
      const [sortField, sortDir] = sort.split("=");
      if (sortField && sortDir) {
        payload.sorter = {
          [sortField === "triggeredAt" ? "createdAt" : sortField]: parseInt(sortDir, 10),
        };
      }
    } else {
      payload.sorter = { createdAt: -1 };
    }

    if (cursor) payload.lastSeenId = cursor;
    if (filter && filter !== "all") payload.status = filter.toLowerCase();
    if (searchValue) payload.search = searchValue;

    getNotificationActivityLogs(
      payload,
      (resStatus, responseString, resJson) => {
        if (!append) closeSpinner();
        setIsLoadingMore(false);

        if (resStatus === 401) {
          modal(sessionExpired);
          toScreen(false, navigate, "/logout");
        } else if (resStatus === "400") {
          if (!append) setData([]);
          alert(responseString);
        } else if (resStatus === 200 || resStatus === "200") {
          const fetched =
            resJson?.payload?.logs ||
            resJson?.payload ||
            resJson?.logs ||
            resJson?.data ||
            [];

          const nextCursorVal =
            resJson?.payload?.nextCursor ||
            resJson?.payload?.lastSeenId ||
            resJson?.nextCursor ||
            resJson?.lastSeenId ||
            null;
          const hasNextVal =
            resJson?.payload?.hasNextPage ||
            resJson?.payload?.hasMore ||
            resJson?.hasNextPage ||
            resJson?.hasMore ||
            false;

          if (Array.isArray(fetched) && fetched.length > 0) {
            const processed = fetched.map((item) => ({
              channel:           item?.channelId || "-",
              recipient:         item?.recepient?.platform || "-",
              status:            item?.status || "-",
              provider:          item?.provider || "-",
              providerMessageId: item?.providerMsgId || "-",
              triggeredAt:       item?.triggeredAt || null,
              _original:         item,
            }));
            if (append) setData((prev) => [...prev, ...processed]);
            else setData(processed);
            setNextCursor(nextCursorVal);
            setHasNextPage(hasNextVal);
          } else if (!append) {
            setData([]);
            setNextCursor(null);
            setHasNextPage(false);
          }
        }
      },
      userDetails.jwttoken,
    );
  }, [filter, searchValue, sort, userDetails.jwttoken, navigate]);

  useEffect(() => {
    setData([]);
    setCursorStack([null]);
    setNextCursor(null);
    setHasNextPage(false);
    fetchData(null, false);
  }, [fetchData]);

  const handleTableScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollTop + clientHeight >= scrollHeight - 100 &&
      hasNextPage && nextCursor && !isLoadingMore
    ) {
      const newStack = [...cursorStack, nextCursor];
      setCursorStack(newStack);
      fetchData(nextCursor, true);
    }
  };

  const handlePreviousPage = () => {
    if (cursorStack.length > 1) {
      const newStack = cursorStack.slice(0, -1);
      setCursorStack(newStack);
      fetchData(newStack[newStack.length - 1], false);
    }
  };

  const handleNextPage = () => {
    if (hasNextPage && nextCursor && !isLoadingMore) {
      const newStack = [...cursorStack, nextCursor];
      setCursorStack(newStack);
      fetchData(nextCursor, false);
    }
  };

  const handleSearchSubmit = () => {
    setSearchValue(inputSearchValue);
    setCursorStack([null]);
    setNextCursor(null);
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
    setCursorStack([null]);
    setNextCursor(null);
  };

  const sortData = (field) => {
    setCursorStack([null]);
    setNextCursor(null);
    setSort(sort === `${field}=1` ? `${field}=-1` : `${field}=1`);
  };

  const getSortClass = (key) => {
    if (sort === `${key}=1`) return "c-list-filter__button c-list-filter__button--icon-up";
    if (sort === `${key}=-1`) return "c-list-filter__button c-list-filter__button--icon-down";
    return "c-list-filter__button c-list-filter__button--faded";
  };

  const renderCell = (item, field) => {
    const value = item[field.key];
    if (field.key === "channel")
      return <span className="">{value}</span>;
    if (field.key === "status")
      return <span className={`badge ${STATUS_BADGE[value] || "badge-secondary"}`}>{value}</span>;
   if (field.key === "providerMessageId") {
  if (!value || value === "-") return <span>-</span>;

  const tooltipId = `nl-provmsg-${item.channel}-${item.triggeredAt}`;

  return (
    <>
      <span
        data-tooltip-id={tooltipId}
        className="description-trigger provider-message-id"
      >
        {String(value)}
      </span>

      <Tooltip
        id={tooltipId}
        content={String(value)}
        place="top"
      />
    </>
  );
}

    if (field.key === "triggeredAt") {
      if (!value) return <span>-</span>;
      const m = moment(value);
      return (
        <>
          <span className="u-before-icon-calendar2">
            &nbsp;&nbsp;{m.format("DD MMM YYYY")}
          </span>
          <br />
          <span className="u-before-icon-clock">
            &nbsp;&nbsp;{m.format("hh:mm A")}
          </span>
        </>
      );
    }
    if (value === null || value === undefined || value === "-") return <span>-</span>;
    if (typeof value === "object") return <span>{Object.values(value)[0] || JSON.stringify(value)}</span>;
    if (typeof value === "boolean") return <span>{value ? "Yes" : "No"}</span>;
    return <span>{String(value)}</span>;
  };


  return (
    <>
          {showDetails && <div className="modal-backdrop"></div>}

      <div className="c-subheader-info-panel">
        <div className="c-subheader-info-panel__search">
          <form onSubmit={(e) => { e.preventDefault(); handleSearchSubmit(); }}>
            <input
              type="text"
              value={inputSearchValue}
              onChange={(e) => setInputSearchValue(e.target.value)}
              placeholder="Search notification logs..."
              className="c-subheader-info-panel__input"
            />
            <button
              type="submit"
              className="c-btn-outline c-btn-outline--white c-btn-outline--small"
            >
              Search
            </button>
          </form>
        </div>
      </div>

      <div className="c-hr"></div>

      <div className="l-row">
        <div className="l-col-100 u-padding-top-0 u-padding-bottom-0">
          <div className="filter-bar filter-bar--styled">
            {FILTER_OPTIONS.map((option) => (
              <div key={option.id} className="c-checkbox-radio-block">
                <input
                  type="radio"
                  id={option.id}
                  name="notificationLogsStatus"
                  value={option.value}
                  onChange={handleFilterChange}
                  checked={filter === option.value}
                />
                <label htmlFor={option.id}>{option.label}</label>
              </div>
            ))}
          </div>
        </div>

        <div className="l-col-100">
          <div className="c-hr u-margin-bottom-0"></div>

          <div className="notification-list-table">
            <div className="notification-table-scroll-wrapper">
              <div className="c-list-entry c-list-entry__header align-items-center">
                {COLUMNS.map((col) => (
                  <div key={col.key} className={col.class}>
                    {col.sortable === false ? (
                      <span style={{ opacity: 0.6 }}>{col.label}</span>
                    ) : (
                      <div className="c-list-filter">
                        <button
                          className={getSortClass(col?.key)}
                          onClick={() => sortData(col?.key)}
                        >
                          {col?.label}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                <div className="nl-col-actions c-list-filter">
                  <button className="c-list-filter__button--faded" style={{ opacity: 0.6 }}>Actions</button>
                </div>
              </div>

              <div className="notification-scroll-list" onScroll={handleTableScroll}>
                {data?.length > 0 ? (
                  data?.map((item, index) => (
                    <div className="c-list-entry align-items-center" key={index}>
                      {COLUMNS.map((field) => (
                        <div key={field.key} className={field.class}>
                          {renderCell(item, field)}
                        </div>
                      ))}
                      <div className="nl-col-actions">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => { setSelectedLog(item); setShowDetails(true); }}
                        >
                          View
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="u-text-align-center u-margin-top-bottom-m u-color-red">
                    No data found.
                  </div>
                )}
                {isLoadingMore && (
                  <div className="scroll-load-more">
                    <div className="scroll-load-more__spinner"></div>
                    <p className="scroll-load-more__text">Loading more...</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="c-hr u-margin-bottom-0"></div>
        </div>
        <div style={{ clear: "both" }}></div>
      </div>

      {/* <div className="cursor-pagination">
        <button
          className="btn btn-outline-primary cursor-pagination__btn"
          onClick={handlePreviousPage}
          disabled={cursorStack.length <= 1}
        >
          ← Previous
        </button>
        <button
          className="btn btn-primary cursor-pagination__btn"
          onClick={handleNextPage}
          disabled={!hasNextPage || !nextCursor || isLoadingMore}
        >
          Next →
        </button>
      </div> */}

      {showDetails && (
        <NotificationLogDetails
          notificationDetails={selectedLog}
          onClose={() => { setShowDetails(false); setSelectedLog(null); }}
        />
      )}
    </>
  );
};

NotificationLogsTab.propTypes = {
  userDetails: PropTypes.object.isRequired,
  navigate:    PropTypes.func.isRequired,
};

export default NotificationLogsTab;
